import React from "react";
import { useI18n } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Globe, Check } from "lucide-react";

const LANGS = [
  { code: "it", label: "Italiano" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

/**
 * Ultra-compact language switcher used in the top bar.
 *
 * Renders a tiny 32px pill that shows the currently selected language as a
 * 2-letter code (IT / EN / FR / DE / ES / PT) preceded by a small globe
 * glyph. On tap it opens a dropdown with the six native language names and
 * a checkmark next to the active one. Selecting a language calls
 * `setLang(code)` — the existing i18n system handles persistence in
 * `localStorage.tc_lang` and the immediate re-render of the whole UI.
 *
 * The trigger deliberately stays small (32×32 tap target, ≤ 56 px wide on
 * mobile) so it fits next to the branding block, the "Sostieni" CTA and
 * "Registrati" button without altering header height or forcing overflow.
 */
export default function LanguageSelect({ testid = "lang-select", className = "" }) {
  const { lang, setLang } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testid}
        aria-label="Language"
        className={`inline-flex items-center gap-1 h-8 px-2 rounded-full border border-slate-300 bg-white/80 hover:bg-white text-[11px] font-semibold text-slate-700 uppercase tracking-wide leading-none shrink-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${className}`}
      >
        <Globe size={12} className="text-slate-500" />
        <span data-testid={`${testid}-current`}>{lang.toUpperCase()}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`${testid}-opt-${l.code}`}
            onSelect={() => setLang(l.code)}
            className="text-xs cursor-pointer"
          >
            <span className="w-5 inline-flex items-center justify-center">
              {lang === l.code && <Check size={12} className="text-emerald-600" />}
            </span>
            <span className="uppercase tracking-wide text-slate-500 mr-2 w-6 text-[10px]">{l.code}</span>
            <span className="text-slate-800">{l.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
